/**
 * Langfuse Service
 *
 * Service singleton pour gérer l'interaction avec Langfuse.
 * Permet d'envoyer des feedbacks utilisateurs (scores) sur les messages AT1.
 */

/**
 * ============================================================================
 * AB TEST: Maturity AT1 Feedback Experiment (ab_at1_feedback)
 * PR: #2501
 *
 * This file is part of an A/B test experiment for collecting user feedback
 * on automatically generated AT1 messages. When the experiment concludes,
 * this entire file should be either:
 * - Removed if the experiment is unsuccessful
 * - Renamed (remove AB_ prefix) and integrated permanently if successful
 *
 * To find all AB test code: grep -r "ab_at1_feedback" src/
 * ============================================================================
 */

import { LangfuseWeb } from 'langfuse';

import { getEnv } from '../utilities/env';

let langfuseInstance: LangfuseWeb | null = null;

/**
 * Génère l'ID de trace Langfuse pour un message AT1.
 *
 * @param tripProjectId - ID du trip project depuis maturityExperiments
 * @returns traceId au format "at1_<tripProjectId>"
 */
export const generateAT1TraceId = (tripProjectId: string): string => {
    return `at1_${tripProjectId}`;
};

/**
 * Récupère l'instance singleton du client Langfuse
 */
export const getLangfuseClient = (): LangfuseWeb => {
    if (!langfuseInstance) {
        const publicKey = getEnv('LANGFUSE_PUBLIC_KEY');
        const baseUrl = getEnv('LANGFUSE_BASE_URL');
        if (!publicKey) {
            throw new Error('[Langfuse] LANGFUSE_PUBLIC_KEY environment variable is not defined');
        }
        if (!baseUrl) {
            throw new Error('[Langfuse] LANGFUSE_BASE_URL environment variable is not defined');
        }

        langfuseInstance = new LangfuseWeb({
            publicKey,
            baseUrl,
        });
    }

    return langfuseInstance;
};

/**
 * Envoie un score de feedback utilisateur à Langfuse.
 *
 * @param traceId - ID du trace Langfuse (généré via generateAT1TraceId)
 * @param value - 1 pour 👍 (positif), 0 pour 👎 (négatif)
 * @param comment - Commentaire optionnel de l'utilisateur
 * @param metadata - Métadonnées contextuelles (dossierId, agentId, agencyId)
 */
export const sendAT1Feedback = async (
    traceId: string,
    value: 1 | 0,
    comment?: string,
    metadata?: {
        dossier_id?: string;
        agent_id?: number;
        agency_id?: number;
    },
): Promise<void> => {
    try {
        const langfuse = getLangfuseClient();

        await langfuse.score({
            traceId,
            name: 'user_feedback',
            value,
            ...(comment && { comment: comment.trim() }),
            ...(metadata && { metadata }),
        });
    } catch (error) {
        console.error('[AT1 Feedback] Error sending to Langfuse:', error);
        throw error;
    }
};
